import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DropdownChevron from '@components/DropdownChevron';
import { FieldSchemaIdTooltip } from '@components/FieldCdrId';
import { Show } from '@components/Show';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { ValidationStateIcon } from '@components/ValidationStateIcon/ValidationStateIcon';
import { useEntityStore, useSchemaStore } from '@ContractBuilder/store';
import { getRootSectionId } from '@ContractBuilder/utils/is-incomplete';
import { useFieldSchemaIds } from '@hooks/use-schema-ids';
import { mdiHelpCircleOutline } from '@mdi/js';
import { isTemplatePath } from '@utils/app-paths';
import { scrollIntoView } from '@utils/scroll-into-view';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';

import H4 from '../../H4';

import { expandSectionAnimationProps } from './constants';

interface CollapsibleItemProps {
  cdrId?: string | string[];
  cdrName?: string | string[];
  mrcId?: string;
  startCollapsed?: boolean;
  ownKey: string;
  className?: string;
  id: string;
  children?: ReactNode;
  title?: ReactNode;
  helperText?: string;
  isRequired?: boolean;
}

const CollapsibleItem: FC<CollapsibleItemProps> = ({
  cdrId,
  cdrName,
  mrcId,
  className,
  id,
  isRequired = false,
  ownKey,
  startCollapsed = false,
  children,
  title,
  helperText,
}) => {
  const { schemaId, parsedCdrIds, parsedCdrNames } = useFieldSchemaIds({ cdrId, cdrName, mrcId });
  const { toggleSectionCollapsed, collapseSection, expandSection, isSectionCollapsed, sectionExists } = useEntityStore(
    (state) => state,
  );

  const { getSectionValidationState } = useSchemaStore(({ getSectionValidationState }) => ({
    getSectionValidationState,
  }));

  const { hasErrors, hasValues, errors = [] } = getSectionValidationState(ownKey);
  const rootParentSectionId = getRootSectionId(ownKey);
  const { hasErrors: rootParentHasErrors } = getSectionValidationState(rootParentSectionId);

  const isTemplate = isTemplatePath();
  const isValid = !hasErrors && hasValues;
  const inProgress = hasValues;
  const isParentDirty = rootParentHasErrors && isRequired;

  const handleCollapse = () => toggleSectionCollapsed(id);

  const collapsed = isSectionCollapsed(id);
  const collapseTouched = sectionExists(id);

  // A `#<ownKey>` hash (how a contract-view schedule mention hands over to the form) opens the
  // named section and every section above it, whatever startCollapsed or an earlier collapse say.
  const hashTarget = useLocation().hash.slice(1);
  const isHashTarget = hashTarget === ownKey;
  const isOnHashPath = isHashTarget || hashTarget.startsWith(`${ownKey}.`);

  useEffect(() => {
    if (collapseTouched || collapsed || isOnHashPath) {
      return;
    }

    if (startCollapsed) {
      collapseSection(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only care about `id` here
  }, [id]);

  // Instant, not smooth: a smooth scroll is cancelled by the next one (ScrollToAnchor fires 100ms later).
  const scrollToSelf = () => scrollIntoView(ownKey, { behavior: 'auto' });
  const hashScrollSettled = useRef(false);

  useEffect(() => {
    if (!isOnHashPath) {
      return;
    }

    expandSection(id);

    if (isHashTarget) {
      scrollToSelf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollToSelf only closes over ownKey
  }, [expandSection, id, isHashTarget, isOnHashPath, ownKey]);

  // The sections above this one start collapsed and animate shut on the same timeline as this one
  // animates open, which drags the first scroll's target off the top of the viewport; scroll once
  // more when the animation settles.
  const handleAnimationComplete = () => {
    if (!isHashTarget || hashScrollSettled.current) {
      return;
    }

    hashScrollSettled.current = true;
    scrollToSelf();
  };

  useEffect(() => {
    if (!collapseTouched || !collapsed) {
      return;
    }

    if (!startCollapsed) {
      expandSection(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only care about `startCollapsed` here
  }, [startCollapsed]);

  return (
    <div
      id={ownKey}
      className={clsx(
        'relative mb-5 rounded-lg bg-white p-6 py-6 shadow-sm transition-all',
        !isRequired && collapsed && !hasValues && 'opacity-50',
        className,
      )}
    >
      <H4
        className={clsx('flex cursor-pointer items-center justify-between', !collapsed && 'mb-3')}
        onClick={handleCollapse}
      >
        <div className="flex items-center gap-2">
          <ValidationStateIcon
            id={ownKey}
            isDirty={inProgress}
            isValid={isValid}
            isParentDirty={isParentDirty}
            isPopulatedMode={isTemplate}
            className="bg-transparent!"
            idleStateNode={<div className="border-info-300 h-5 w-5 rounded-full border-4">&nbsp;</div>}
            errors={errors}
          />
          <FieldSchemaIdTooltip
            schemaId={schemaId}
            cdrIds={parsedCdrIds}
            cdrNames={parsedCdrNames}
            fallbackName={title}
          >
            <span>{title}</span>
          </FieldSchemaIdTooltip>
          <Show when={!!helperText}>
            <TooltipedIcon
              path={mdiHelpCircleOutline}
              data-testid={`${id}:helper-text`}
              placement="top"
              content={helperText as string}
              className="mb-0.5"
            />
          </Show>
        </div>
        <DropdownChevron open={collapsed} size={1} />
      </H4>
      <AnimatePresence>
        {!collapsed ? (
          <motion.div
            {...expandSectionAnimationProps}
            key={`collapsible-item-${id}`}
            onAnimationComplete={handleAnimationComplete}
          >
            {children}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleItem;
